import { useState, useCallback } from 'react';
import { message } from 'antd';
import { j5m3Api, EligibleItem, SearchResponse, ExecuteResponse, SearchFilter } from '../../../services/j5m3Api';

export interface UseJ5M3ReversalReturn {
  loading: boolean;
  executing: boolean;
  items: EligibleItem[];
  total: number;
  selectedKeys: string[];
  filter: SearchFilter;
  setFilter: (f: SearchFilter) => void;
  setSelectedKeys: (keys: string[]) => void;
  fetchEligible: () => Promise<void>;
  batchExecute: () => Promise<ExecuteResponse | null>;
}

export function useJ5M3Reversal(): UseJ5M3ReversalReturn {
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [items, setItems] = useState<EligibleItem[]>([]);
  const [total, setTotal] = useState(0);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [filter, setFilter] = useState<SearchFilter>({ startNo: '', endNo: '' });

  const fetchEligible = useCallback(async () => {
    if (!filter.startNo || !filter.endNo) {
      message.warning('請輸入單號起始和結束');
      return;
    }
    setLoading(true);
    setSelectedKeys([]);
    try {
      const resp: SearchResponse = await j5m3Api.search(filter);
      const list = resp.items || [];
      setItems(list);
      setTotal(resp.total || 0);
    } catch (err: unknown) {
      const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '查詢失敗');
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const batchExecute = useCallback(async (): Promise<ExecuteResponse | null> => {
    if (selectedKeys.length === 0) {
      message.warning('請至少勾選一筆單據進行沖銷');
      return null;
    }
    setExecuting(true);
    try {
      const resp = await j5m3Api.execute(selectedKeys);
      message.success(resp.message);
      await fetchEligible();
      return resp;
    } catch (err: unknown) {
      const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '沖銷失敗');
      message.error(msg);
      return null;
    } finally {
      setExecuting(false);
    }
  }, [selectedKeys, fetchEligible]);

  return {
    loading,
    executing,
    items,
    total,
    selectedKeys,
    filter,
    setFilter,
    setSelectedKeys,
    fetchEligible,
    batchExecute,
  };
}
